'use client'

import { useEffect, useState } from 'react'

import { format, formatDistanceToNow } from 'date-fns'
import { motion } from 'framer-motion'
import {
  AlertCircle,
  Bell,
  Calendar,
  CheckCircle,
  Clock,
  Edit,
  Heart,
  Star,
  Timer,
  Users,
} from 'lucide-react'

import { Registration } from '../../lib/workshop-api'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import { Progress } from '../ui/progress'

interface WaitlistStatusProps {
  registration: Registration | null
  waitlistPosition?: number | null
  spotOfferExpiry?: Date | null
  onJoinWaitlist: () => void
  onAcceptSpot: () => void
  onDeclineSpot: () => void
  onEditPreferences: () => void
  loading?: boolean
}

export function WaitlistStatus({
  registration,
  waitlistPosition,
  spotOfferExpiry,
  onJoinWaitlist,
  onAcceptSpot,
  onDeclineSpot,
  onEditPreferences,
  loading = false,
}: WaitlistStatusProps) {
  const [timeRemaining, setTimeRemaining] = useState<string>('')

  // Countdown timer for spot offer expiry
  useEffect(() => {
    if (!spotOfferExpiry) return

    const timer = setInterval(() => {
      const now = new Date()
      const expiry = new Date(spotOfferExpiry)

      if (expiry > now) {
        setTimeRemaining(formatDistanceToNow(expiry))
      } else {
        setTimeRemaining('Expired')
        clearInterval(timer)
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [spotOfferExpiry])

  const isSpotOffered = registration?.currentState === 'spotOffered'
  const isOnWaitlist = registration?.currentState === 'onWaitlist'
  const isWaitlistOnly = registration?.currentState === 'waitlistOnly'

  const getWaitlistProgress = () => {
    if (!waitlistPosition) return 0
    // Assuming average waitlist size of 20 for visualization
    const totalEstimated = Math.max(waitlistPosition + 10, 20)
    return ((totalEstimated - waitlistPosition) / totalEstimated) * 100
  }

  // Spot Offered State
  if (isSpotOffered && spotOfferExpiry) {
    const isExpired = new Date(spotOfferExpiry) <= new Date()

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        <Card
          className={`p-6 text-center ${isExpired ? 'dark:bg-red-900/20 border-red-200 bg-red-50' : 'dark:bg-green-900/20 border-green-200 bg-green-50'}`}
        >
          <div
            className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${isExpired ? 'dark:bg-red-900/30 bg-red-100' : 'dark:bg-green-900/30 bg-green-100'}`}
          >
            {isExpired ? (
              <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            ) : (
              <Star className="h-8 w-8 text-green-600 dark:text-green-400" />
            )}
          </div>

          {isExpired ? (
            <>
              <h2 className="text-2xl font-semibold text-red-700 dark:text-red-400">
                Spot Offer Expired
              </h2>
              <p className="mt-2 text-red-600 dark:text-red-300">
                The workshop spot offer has expired. You'll remain on the waitlist.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-semibold text-green-700 dark:text-green-400">
                Workshop Spot Available!
              </h2>
              <p className="mt-2 text-green-600 dark:text-green-300">
                A spot has opened up in your preferred workshop.
              </p>
            </>
          )}
        </Card>

        {!isExpired && (
          <Card className="p-6">
            <div className="space-y-4 text-center">
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Time to Decide</h3>
                <div className="text-3xl font-bold text-shuttle-red">{timeRemaining}</div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  remaining to accept this spot
                </p>
              </div>

              <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
                <h4 className="mb-2 font-medium text-blue-900 dark:text-blue-100">
                  Workshop Details
                </h4>
                {registration?.selectedWorkshops?.map((workshop, index) => (
                  <div key={index} className="text-sm text-blue-800 dark:text-blue-200">
                    <div className="font-medium">{workshop.title}</div>
                    <div className="capitalize">{workshop.format} format</div>
                  </div>
                ))}
              </div>

              <div className="flex justify-center space-x-4 pt-4">
                <Button
                  onClick={onAcceptSpot}
                  disabled={loading}
                  size="lg"
                  className="bg-green-600 text-white hover:bg-green-700"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      <span>Accepting...</span>
                    </div>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Accept Spot
                    </>
                  )}
                </Button>

                <Button
                  onClick={onDeclineSpot}
                  variant="outline"
                  size="lg"
                  className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300"
                >
                  Decline
                </Button>
              </div>
            </div>
          </Card>
        )}
      </motion.div>
    )
  }

  // On Waitlist State
  if (isOnWaitlist) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        <Card className="border-blue-200 bg-blue-50 p-6 text-center dark:bg-blue-900/20">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
            <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-2xl font-semibold text-blue-700 dark:text-blue-400">
            You're on the Waitlist!
          </h2>
          <p className="mt-2 text-blue-600 dark:text-blue-300">
            We'll notify you as soon as a spot becomes available.
          </p>
        </Card>

        <Card className="p-6">
          <h3 className="mb-4 text-lg font-semibold">Waitlist Status</h3>

          <div className="space-y-6">
            {/* Position */}
            {waitlistPosition && (
              <div className="text-center">
                <div className="mb-2 text-4xl font-bold text-shuttle-red">#{waitlistPosition}</div>
                <p className="text-gray-600 dark:text-gray-400">Your position in the waitlist</p>

                <div className="mt-4">
                  <Progress value={getWaitlistProgress()} className="h-3" />
                  <p className="mt-2 text-sm text-gray-500">Progress to front of waitlist</p>
                </div>
              </div>
            )}

            {/* Workshop Preferences */}
            <div>
              <h4 className="mb-3 font-medium">Your Preferences</h4>
              <div className="space-y-2">
                {registration?.selectedWorkshops?.map((workshop, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-lg bg-gray-50 p-3 dark:bg-gray-800"
                  >
                    <div className="flex items-center space-x-3">
                      <Badge
                        variant="outline"
                        className="flex h-8 w-8 items-center justify-center rounded-full"
                      >
                        {workshop.preference}
                      </Badge>
                      <div>
                        <div className="font-medium">{workshop.title}</div>
                        <div className="text-sm capitalize text-gray-600 dark:text-gray-400">
                          {workshop.format} format
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* What happens next */}
            <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
              <h4 className="mb-2 font-medium text-blue-900 dark:text-blue-100">
                What happens next?
              </h4>
              <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
                <li>• We'll check for cancellations and new spots daily</li>
                <li>• You'll be notified immediately when a spot opens</li>
                <li>• You'll have 24 hours to accept the offered spot</li>
                <li>• Your position improves as others ahead of you get spots</li>
                <li>• You can update your preferences at any time</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={onEditPreferences}
                className="flex items-center space-x-2"
              >
                <Edit className="h-4 w-4" />
                <span>Update Preferences</span>
              </Button>

              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                <Bell className="h-4 w-4" />
                <span>Notifications enabled</span>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
    )
  }

  // Waitlist Options State (initial waitlist decision)
  if (isWaitlistOnly) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        <Card className="bg-orange-50 dark:bg-orange-900/20 border-orange-200 p-6 text-center">
          <div className="bg-orange-100 dark:bg-orange-900/30 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
            <Calendar className="h-8 w-8 text-orange-600 dark:text-orange-400" />
          </div>
          <h2 className="text-2xl font-semibold text-orange-700 dark:text-orange-400">
            Workshops Currently Full
          </h2>
          <p className="mt-2 text-orange-600 dark:text-orange-300">
            Your preferred workshops are at capacity, but you have options.
          </p>
        </Card>

        <Card className="p-6">
          <h3 className="mb-4 text-lg font-semibold">Your Workshop Preferences</h3>

          <div className="mb-6 space-y-3">
            {registration?.selectedWorkshops?.map((workshop, index) => (
              <div
                key={index}
                className="flex items-center justify-between rounded-lg bg-gray-50 p-3 dark:bg-gray-800"
              >
                <div className="flex items-center space-x-3">
                  <Badge
                    variant="outline"
                    className="flex h-8 w-8 items-center justify-center rounded-full"
                  >
                    {workshop.preference}
                  </Badge>
                  <div>
                    <div className="font-medium">{workshop.title}</div>
                    <div className="text-sm capitalize text-gray-600 dark:text-gray-400">
                      {workshop.format} format
                    </div>
                  </div>
                </div>
                <Badge className="dark:bg-red-900/20 bg-red-100 text-red-700 dark:text-red-300">
                  Full
                </Badge>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Join Waitlist */}
              <div className="rounded-lg border-2 border-blue-200 p-4">
                <div className="mb-3 flex items-center space-x-3">
                  <Users className="h-6 w-6 text-blue-600" />
                  <h4 className="font-medium">Join the Waitlist</h4>
                </div>
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                  Get notified when spots become available in your preferred workshops.
                </p>
                <div className="space-y-2 text-sm text-blue-600 dark:text-blue-400">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4" />
                    <span>Free to join</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Bell className="h-4 w-4" />
                    <span>Instant notifications</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Timer className="h-4 w-4" />
                    <span>24h to respond to offers</span>
                  </div>
                </div>
                <Button
                  onClick={onJoinWaitlist}
                  disabled={loading}
                  className="mt-4 w-full bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      <span>Joining...</span>
                    </div>
                  ) : (
                    'Join Waitlist'
                  )}
                </Button>
              </div>

              {/* Modify Preferences */}
              <div className="rounded-lg border-2 border-gray-200 p-4">
                <div className="mb-3 flex items-center space-x-3">
                  <Edit className="h-6 w-6 text-gray-600" />
                  <h4 className="font-medium">Modify Preferences</h4>
                </div>
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                  Choose different workshops or formats that might have availability.
                </p>
                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4" />
                    <span>See all workshops</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>Different dates</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4" />
                    <span>Online options</span>
                  </div>
                </div>
                <Button onClick={onEditPreferences} variant="outline" className="mt-4 w-full">
                  Update Preferences
                </Button>
              </div>
            </div>

            {/* Information */}
            <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
              <div className="flex items-start space-x-3">
                <Heart className="mt-0.5 h-5 w-5 text-blue-600 dark:text-blue-400" />
                <div>
                  <h4 className="mb-1 font-medium text-blue-900 dark:text-blue-100">
                    Popular workshops fill up quickly!
                  </h4>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    Many participants find spots through our waitlist as plans change. We typically
                    see 15-20% turnover before workshop dates.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
    )
  }

  return null
}
